import type { GetPaymentMethodsData, PromoSummary, UpdateItemPayload } from '#root/api/clients/cart/data-contracts'
import type { GetStoreInventoryData } from '#root/api/clients/product/data-contracts'

import type { ApiError } from '#root/api/util/error'
import type { CartStoreAddress } from '#types/applePay'
import type {
  CartGroupedItems,
  CartItem,
  CartStoreContext,
  CartTotals,
  CartUpsellProduct,
  CollectionPoint,
  Coupon,
  CustomerNotification,
  DeliveryOptionsStorage,
  FlashError,
  FlashErrorDetails,
  FlashErrorType,
  GiftOption,
  NotificationLevel,
  PaymentInstrument,
  SavedItems,
  ShippingAddress,
  ShippingMethod,
  ShippingMethodCode
} from '#types/cart'
import type { PaymentMethodCode } from '#types/components/checkout/paymentMethods'
import type { ProductContext } from '#types/gtm'
import type { BaseNotification } from '#types/notification'
/**
 * @remark Store could be anything like user, cart, screen.
 * The first argument is a unique id of the store across your application
 */
import { ApiErrorCode } from '#root/enums/api'

export const useCartStore = (
  storeType: CartStoreContext = '',
  countryCode = useCountryCode()
) => defineStore(`cart${storeType}_${countryCode}`, () => {
  const { cart, products, wishlists } = useApi()
  const config = useAppConfig()
  const { $feature } = useNuxtApp()
  const { savedForLaterPageSize } = config.pages.cart.savedForLater
  const { sortBonusItemsFirst } = config.components.checkout.cartSummary
  const { loyaltyType } = config.pages.checkout
  const { DialogMiniCart } = useDialogsStore()
  const {
    allowPickupOrder,
    enableRelativePdpUrl,
    enableShippingAddressRecaptcha,
    showMiniCartOnAddProduct,
    enableApproachingDiscounts,
    enableClearCollectionPointOnCartChange
  } = useFeatureFlags()
  const { $dtrum, $formatPrice, $t } = useNuxtApp()
  const toast = useToaster()
  const route = useRoute()
  const buyInStore = useBuyInStoreStore()
  const approachingPromotions = useApproachingPromotionsStore()
  const deliveryOptions = useSessionStorage('deliveryOptions', { collectionPoint: {}, shipToHome: {} } as DeliveryOptionsStorage)

  /**
   * Clears the selected collection point from checkout delivery options when enabled, so checkout
   * does not show a stale location after SFCC drops the basket shipping address on line-item CRUD.
   * Preserves `shipToHome` used when switching delivery tabs.
   */
  const clearCollectionPointFromSessionStorage = () => {
    if (!enableClearCollectionPointOnCartChange || !deliveryOptions.value.collectionPoint?.storeName) return
    deliveryOptions.value.collectionPoint = {} as CollectionPoint
  }

  const basketId = ref('')
  const currency = ref<string>(useCurrencyCode())
  const totalItems = ref(0)
  const totals = ref<CartTotals>({
    total: 0,
    totalDiscount: 0,
    itemTotal: 0,
    totalWithoutTax: 0,
    shipping: 0,
    tax: 0,
    totalDiscountPercentage: 0,
    remainingToPay: 0,
    currency: '',
    totalUnits: 0,
    itemSubTotal: 0,
    netAmount: 0,
    taxAmount: 0,
    appliedRewards: 0
  })
  const items = ref<CartItem[]>([])
  const savedItems = ref<SavedItems>({} as SavedItems)
  const shippingMethods = ref<ShippingMethod[]>([])
  const paymentInstruments = ref<PaymentInstrument[]>([])
  const paymentMethods = ref<GetPaymentMethodsData>([])
  const paymentMethodsPending = ref(false)
  const couponItems = ref<Coupon[]>([])
  const orderPromotions = ref<any>([])
  const promoSummary = ref<PromoSummary[]>([])
  const upsellProducts = ref<CartUpsellProduct[]>([])
  const pending = ref(false)
  const pendingShippingAddress = ref(false)
  const loadingApplicableShippingMethods = ref(false)
  const errorMessage = ref('')
  const giftCardsBalances = ref(new Map<string, number>())
  const favoriteStoreId = ref('')
  const cachedCustomerNotifications = ref<Record<string, CustomerNotification[]>>({})
  const isCartMerged = ref(false)
  const savedCartPending = ref(false)
  const selectedUpsells = ref<string[]>([])
  const notification = reactive<BaseNotification & { level: NotificationLevel }>({
    type: '',
    message: '',
    level: 'top'
  })

  /**
   * Set the active cart notification (banner / inline / toast). Auto-scrolls to top when the level is 'top'.
   * Suppresses the duplicate generic OOS message to avoid clobbering more specific item-level errors.
   * @param type - notification severity ('error' | 'success' | 'info' | …)
   * @param message - localized message body
   * @param level - placement: 'top' | 'item' | 'input' (defaults to whatever was set previously)
   */
  const setNotification = (type: BaseNotification['type'], message: string, level?: NotificationLevel) => {
    if (message === $t.outOfStockNotification) return

    notification.type = type
    notification.message = message
    if (level) notification.level = level

    if (message && notification.level === 'top') {
      scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }

  /** Clear the active cart notification (resets type and message; preserves level). */
  const clearNotification = () => {
    notification.type = ''
    notification.message = ''
  }

  const flash = ref<FlashError[]>([])
  // out-of-stock items coming from flash messages
  const outOfStockItems = ref<(FlashErrorDetails & { reasonMessage?: string })[]>([])
  // check for the context of the pinia store to decide on passing a necessary query parameter
  // it can be done with switch if you need more contexts in the future
  const queryParams = computed(() =>
    storeType === 'applePayPdp' || storeType === 'paypalPdp' ? { bid: 'cookie' } : undefined
  )

  /**
   * Wipe all cart state back to defaults. Does NOT call the backend - call sites pair this with
   * `cart.$create` (see `recreateCart`) when the basket on the server is also being replaced.
   */
  const reset = () => {
    basketId.value = ''
    currency.value = useCurrencyCode()
    totalItems.value = 0
    totals.value = {
      total: 0,
      totalDiscount: 0,
      itemTotal: 0,
      totalWithoutTax: 0,
      shipping: 0,
      tax: 0,
      totalDiscountPercentage: 0,
      remainingToPay: 0,
      currency: '',
      totalUnits: 0,
      itemSubTotal: 0,
      netAmount: 0,
      taxAmount: 0,
      appliedRewards: 0
    }
    items.value = []
    savedItems.value = {} as SavedItems
    shippingMethods.value = []
    paymentInstruments.value = []
    paymentMethods.value = []
    paymentMethodsPending.value = false
    orderPromotions.value = []
    promoSummary.value = []
    upsellProducts.value = []
    pending.value = false
    pendingShippingAddress.value = false
    loadingApplicableShippingMethods.value = false
    errorMessage.value = ''
    notification.message = ''
    giftCardsBalances.value = new Map()
    outOfStockItems.value = []
    approachingPromotions.reset()
  }

  /**
   * Drop all flash messages of a given code. No-op if none match.
   * @param code - flash error type to remove (e.g. 'ProductLineItemInventoryMissing')
   */
  const resetFlashMsg = (code: FlashErrorType) => {
    if (flash.value.some((msg) => msg.code === code))
      flash.value = flash.value.filter((msg) => msg.code !== code)
  }

  /**
   * Sets Merge Cart message to toast if needed. Moved out separately due to SQ complexity complaint.
   * @param options - flash-handling options
   * @param options.keepNotifications - retain prior notification state
   */
  const setMergeCartNotification = ({ keepNotifications }) => {
    // if we have OOS items and should keep notifications,
    // we want to keep the merge cart notification as a toast. (https://digital.vfc.com/jira/browse/GLOBAL15-54905)
    if (keepNotifications)
      isCartMerged.value = true
  }
  const setMessagePerCode = (code: FlashErrorType) => (
    // Empty cart on full-inventory-missing = every item dropped during validation; surface
    // "order can't be completed" instead of generic OOS, so the shopper knows nothing remains.
    (code === 'ProductLineItemFullInventoryMissing' && !items.value.length)
      ? $t.orderCanNotBeCompletedOOS
      : $t.outOfStockNotification
  )
  /**
   * Process server flash messages: dedupe OOS items by productId, classify into partial vs full
   * OOS / shipping-method transitions / invalid shipping address, and surface the right notification.
   * @param flashMessages - flash array from a cart response
   * @param keepNotifications - retain prior notification state across this pass
   */
  const handleFlashMessages = (flashMessages: FlashError[], keepNotifications: boolean) => {
    flashMessages.forEach((flashMessage) => {
      const code = flashMessage.code || flashMessage.type
      const productId = flashMessage.details?.sku || flashMessage.details?.productId

      if (outOfStockItems.value.some((item) => item.productId === productId)) return

      const messageLevel: NotificationLevel = 'item'

      if (code && [
        'ProductLineItemInventoryMissing',
        'ProductItemNotAvailable',
        'ProductLineItemFullInventoryMissing'
      ].includes(code) && flashMessage.details?.productId) {
        // need flashMessage.details?.productId here because only valid flash errors have productId
        // otherwise it will be overwritten by empty object
        let additionalData = {}
        const isPartialOos = code === 'ProductLineItemInventoryMissing'
        // grab additional data from the cart for partial oos item (missing in the response inside flash object)
        const mapProductInCart = items.value.find((product) => product.productId === productId)
        if (isPartialOos) {
          additionalData = {
            productSlug: mapProductInCart?.pdpUrl,
            // Lost-value display: price × removed qty so the OOS notification can show what was dropped.
            basePrice: (mapProductInCart?.price?.current ?? 0) * (flashMessage.details?.missingQty ?? 0),
            id: mapProductInCart?.id,
            masterProductId: mapProductInCart?.masterId2,
            variationGroupId: mapProductInCart?.masterId
          }
        }
        outOfStockItems.value.push({
          ...mapProductInCart,
          ...flashMessage.details,
          isPartialOos,
          ...additionalData
        })

        const message = setMessagePerCode(code)
        setMergeCartNotification({ keepNotifications })
        setNotification('error', message, messageLevel)
      }
      else if (code && [
        'PickupToSthTransition',
        'PickupToStsTransition',
        'StsToSthTransition',
        'StsToPickupTransition',
        'STHToPickupTransition'
      ].includes(code) && flashMessage.details?.productId) {
        const mapProductInCart = items.value.find((product) => product.productId === productId)
        outOfStockItems.value.push({
          ...mapProductInCart,
          ...flashMessage.details,
          reasonMessage: $t.itemDeliveryMethodChanged
        })

        setNotification('error', $t.apiMessages.general.ORDER_UPDATED, messageLevel)
      }
      // Only error response (4xx) has the shipmentId property,
      // so we don't show the notification for success response (2xx) even with flash messages in it
      else if (code === 'InvalidShippingAddress' && flashMessage.details?.shipmentId) {
        setNotification('error', $t.apiMessages.general.INVALID_SHIPPING_ADDRESS, messageLevel)
      }
    })
  }

  /**
   * Cart-level recovery: snapshot remaining items, drop the basket, create a fresh one, re-add.
   * Used when the existing basket is unrecoverable (invalid customer, missing cart).
   * @param itemsToRemove - ids of items to exclude from the rehydration
   */
  const recreateCart = async (itemsToRemove?: string[]) => {
    pending.value = true

    try {
      const itemsToBeRemoved = items.value?.filter(({ id }) => !itemsToRemove?.includes(id))
        .map((item) => ({
          ...item,
          recipeId: item.recipeId || undefined, // Non-customs items have an empty string recipeId which breaks the request
          storeId: item.shippingOptions.find(({ selected }) => selected)?.storeInfo?.id,
          ...(item.giftOption.email && item.giftOption.message && item.giftOption.to
            ? getDigitalGiftOption(item.giftOption as GiftOption)
            : {})
        }))

      reset()
      const response = await cart.$create({})

      basketId.value = response?.id || ''
      try {
        await cart.$addItem(itemsToBeRemoved?.map((item) => ({
          ...item,
          cartId: basketId.value
        })))
      }
      catch {
        setNotification('error', $t.cartExpiredMessage)
      }
    }
    catch {
      reset()
    }
    finally {
      pending.value = false
    }
  }

  /**
   * To handle the AUT498 error code for the invalid customer case,
   * the cart needs to be recreated and the page reloaded
   * HOTFIX: https://digital.vfc.com/jira/browse/GLOBAL15-59506
   */
  const clearAndRecreateCart = async () => {
    localStorage.removeItem('cart')
    await recreateCart()
    toast.add({
      autoClose: false,
      props: {
        message: $t.invalidCustomerCart,
        type: 'error'
      }
    })
    setTimeout(() => {
      location.reload()
    }, 4000)
  }

  const getItemsStoreInventory = async (checkFavoriteStoreInventory?: boolean) => {
    try {
      pending.value = true
      // Get cart item store inventory info when the pickup store being selected
      let storeInventoryResponses: GetStoreInventoryData[] = []
      const storeInfoItems = items.value.filter(({ shippingOptions }) =>
        shippingOptions.some(({ storeInfo, selected }) => (selected || checkFavoriteStoreInventory) && storeInfo?.id))
      if (storeInfoItems.length) {
        storeInventoryResponses = await Promise.all(storeInfoItems
          .map((item) => products.$getStoreInventory(
            item.productId,
            [{ storeId: item.shippingOptions.find(({ storeInfo }) => storeInfo)?.storeInfo?.id ?? '' }]
          )))
      }
      items.value = items.value.map((item) => ({
        ...item,
        shippingOptions: item.shippingOptions.map((option) => {
          const storeInventory = storeInventoryResponses.find(({ productId }) => productId === item.productId)
            ?.storeInventory
            ?.find(({ storeId }) => storeId === option.storeInfo?.id)
          return {
            ...option,
            storeInfo: option?.storeInfo
              ? { ...option.storeInfo, storeInventory: storeInventory?.quantity ?? 0 }
              : undefined
          }
        })
      }))
    }
    catch (err) {
      log.error(err)
    }
    finally {
      pending.value = false
    }
  }

  const orderPromoSummary = computed(() => promoSummary.value
    ?.filter(({ level }) => level === 'ORDER')
    .map((promo) => ({
      ...promo,
      calloutMessage: approachingPromotions.splitMessages(promo.calloutMessage)?.[2] || promo.calloutMessage
    }))
  )
  const productPromoSummary = computed(() =>
    promoSummary.value?.filter(({ level }) => level === 'PRODUCT')
  )
  const shippingPromoSummary = computed(() =>
    promoSummary.value?.filter(({ level }) => level === 'SHIPPING')
  )

  /**
   * Hydrate the store from a cart-shaped API response: totals, items, shipping methods, payment
   * instruments, promos, flash. Triggers `cart.onCartUpdate` GTM unless `emitUpdateEvent` is
   * false. When `keepNotifications` is true, retains the previous flash array if the response
   * has none (used by partial-update endpoints that don't return flash).
   * @param response - raw cart payload from any cart endpoint
   * @param options - hydration options
   * @param options.emitUpdateEvent - push GTM cart update (default true)
   * @param options.keepNotifications - retain prior flash if response has none (default false)
   */
  const updateCartData = (response: any, {
    emitUpdateEvent = true,
    keepNotifications = false
  } = {}) => {
    const { $gtm } = useNuxtApp()
    totals.value = {
      ...response.totals,
      totalDiscountPercentage: getPercentage(response.totals.itemTotal, response.totals.totalDiscount)
    }
    totalItems.value = response.totalItems
    currency.value = response.currency
    items.value = (response.items || []).map((item) => transformCartItem(item, { $t, enableRelativePdpUrl }))

    paymentInstruments.value = response.payment_instruments || []
    shippingMethods.value = (response.shippingMethods || []).map((method) => {
      const oldMethod = shippingMethods.value.find(({ shippingId }) => shippingId === method.shippingId)
      const updatedMethod = {
        ...oldMethod,
        ...method,
        shippingPromotions: method.shippingPromotions,
        applicableShippingMethods: method.applicableShippingMethods
          || oldMethod?.applicableShippingMethods
          || [],
        storeId: method.storeId,
        storeName: method.storeName

      }
      if (!method.shippingPromotions?.length) {
        // if new method details does not contain shipping promotions at all, purge the old method's promotions data
        delete updatedMethod.shippingPromotions
      }
      return updatedMethod
    })
    couponItems.value = response.couponItems || []
    orderPromotions.value = response.orderPromotions || []
    upsellProducts.value = response.upsellProducts || []
    // if promo name is not available, use promo id
    promoSummary.value = (response.promoSummary || []).map((promo) =>
      ({ ...promo, promotionName: promo.promotionName || promo.promotionId }))
    // handle flash messages. if we keep notifications keep the previous value
    flash.value = response?.flash || (keepNotifications ? flash.value : [])
    // calling this every time in case we need to clear outOfStockNotification
    handleFlashMessages(flash.value, keepNotifications)
    approachingPromotions.process(response)

    if (emitUpdateEvent)
      $gtm.push('cart.onCartUpdate', response)
  }

  /**
   * Fetch and attach `applicableShippingMethods` to each ME / customs / prime shipping group.
   * Required before payment-instrument operations and rate selection because the cart endpoint
   * does not return the per-method options inline.
   */
  const getApplicableShippingMethods = async () => {
    try {
      loadingApplicableShippingMethods.value = true
      if (!shippingMethods.value.length) return

      const meAndCustomsShippingMethods = shippingMethods.value
        .filter(({ shippingId }) => ['me', 'customs', 'prime'].includes(shippingId))
      const applicableShippingMethodsResp = (await Promise.all(meAndCustomsShippingMethods
        .map(({ shippingId }) => cart.$getApplicableShippingMethods(basketId.value, shippingId, queryParams.value))))
        .map((resp, i) => ({
          ...resp,
          shippingId: meAndCustomsShippingMethods[i].shippingId,
          applicableShippingMethods: resp.shippingMethods?.map((applicableMethod) => ({
            ...applicableMethod,
            selected: applicableMethod.code === resp.defaultShippingMethodId
          })) as ShippingMethod['applicableShippingMethods']
        }))

      shippingMethods.value = shippingMethods.value.map((method) => {
        const found = applicableShippingMethodsResp.find(({ shippingId }) => shippingId === method.shippingId)
        if (!found) return { ...method, applicableShippingMethods: [] }
        const { applicableShippingMethods, customNotifications } = found

        return { ...method, applicableShippingMethods: applicableShippingMethods ?? [], customNotifications }
      })
    }
    finally {
      loadingApplicableShippingMethods.value = false
    }
  }

  /**
   * Switch a single line item between pickup and ship-to-home by updating its assigned store.
   * Closes the mini-cart on completion. Pass an empty `storeId` to drop pickup back to STH.
   * @param item - cart line item to mutate
   * @param storeId - destination store id, or undefined / empty to revert to ship-to-home
   * @param updateInventory - re-run store inventory lookup after the switch
   */
  const updateItemShippingMethod = async (item: CartItem, storeId?: string, updateInventory?: boolean) => {
    try {
      pending.value = true
      const response = await cart.$updateItem(basketId.value, {
        cartId: basketId.value,
        productId: item.productId,
        itemId: item.id,
        qty: item.qty,
        maxQty: item.maxQty,
        storeId: storeId ?? ''
      }, {
        action: 'pickup',
        favStoreId: storeId || item.shippingOptions.find(({ selected }) => selected)?.storeInfo?.id
      })
      updateCartData(response, { emitUpdateEvent: false })
      if (updateInventory) await getItemsStoreInventory()
      errorMessage.value = ''
    }
    catch (err) {
      assertApiError(err)
      setNotification('error', err.message, 'top')
    }
    finally {
      pending.value = false
      DialogMiniCart.close()
    }
  }

  const getFavoriteStoreId = (selectedStoreId: string, isPatchRequired: boolean) =>
    (allowPickupOrder && isPatchRequired) ? selectedStoreId || favoriteStoreId.value : undefined

  const fetchCartData = async (
    favStoreId: string | undefined,
    inventorySupplyCheck: boolean | undefined,
    isPatchRequired: boolean | undefined
  ) => cart.$get(basketId.value, {
    favStoreId: favStoreId || undefined,
    isPatchRequired: !!favStoreId || isPatchRequired || undefined,
    inventorySupplyCheck: inventorySupplyCheck || undefined
  })

  const getExtraInfoMessage = (err) => {
    if (err.errorDetails?.[0]?.message?.length) {
      switch (err.errorDetails[0].message[0].type) {
        case 'ProductItemMaxQuantity':
          return $t.apiMessages.orderUpdatedExtraInfo.ProductItemMaxQuantity
        case 'ProductItemRequired':
          return $t.apiMessages.orderUpdatedExtraInfo[err.errorDetails[0].message[2]?.type]
      }
    }
    return ''
  }

  /**
   * get payment method data from api and update the store
   * uses store basketId to get the payment method
   */
  const getPaymentMethod = async () => {
    try {
      if (basketId.value) {
        paymentMethodsPending.value = true
        paymentMethods.value = await cart.$getPaymentMethods(basketId.value)
      }
    }
    catch (err) {
      log.error(err)
    }
    finally {
      paymentMethodsPending.value = false
    }
  }

  /**
   * get - gets the cart with the latest changes from the server
   * @param params - fetch options (all optional)
   * @param params.inventorySupplyCheck - if set to true will directly request the OMS to validate the inventory. Please use it only on prepareOrder or mergeCart flows
   * @param params.mergeCarts - cart will be merged if set to true
   * @param params.selectedStoreId – value to be set for the query parameter favStoreId when fetching cart data
   * @param params.isPatchRequired - force a patch refresh on the cart endpoint
   * @param params.keepNotifications - should we keep notifications through the first pass in flash messages handler, default is false
   * @param params.isRecreated - indicates if the cart was recreated, to trigger additional actions such as fetching available payment methods for checkout
   * @param params.checkFavoriteStoreInventory - should we check inventory for the store saved as favorite
   */
  const get = async ({
    inventorySupplyCheck = false,
    mergeCarts = false,
    selectedStoreId = favoriteStoreId.value,
    isPatchRequired = false,
    keepNotifications = false,
    isRecreated = false,
    checkFavoriteStoreInventory = false
  } = {}) => {
    pending.value = true
    try {
      if (!basketId.value) {
        // when there is no basketId, we need to create a new empty cart
        const response = await cart.$create({})
        if (response.id)
          basketId.value = response.id
      }

      if (basketId.value) {
        const favStoreId = getFavoriteStoreId(selectedStoreId, isPatchRequired)
        const response = await fetchCartData(favStoreId, inventorySupplyCheck, isPatchRequired)

        updateCartData(response, { emitUpdateEvent: mergeCarts, keepNotifications })
        await getItemsStoreInventory(checkFavoriteStoreInventory)

        // After setting feature flag allowPickupOrder to false, change all pickup items in cart to ship-to-home
        const pickupItems = (response.items || []).filter((item: { shippingOptions: any[] }) =>
          isPickupOrSts(item.shippingOptions?.find(({ selected }) => selected)?.shippingMethod.code))
        if (!allowPickupOrder && pickupItems.length)
          await Promise.all(pickupItems.map((pickupItems: CartItem) => updateItemShippingMethod(pickupItems)))
      }

      if (isRecreated) {
        // with one-click checkout we need to get available payment methods after payment cancellation
        // and getting a new cart with recreated contents
        await getPaymentMethod()
      }
    }
    catch (err) {
      assertApiError(err)

      // Fire-and-forget: clearAndRecreateCart schedules a page reload via setTimeout, so awaiting
      // would block the error notification below from rendering before the reload fires.
      if (err.errorId === ApiErrorCode.INVALID_CUSTOMER)
        await clearAndRecreateCart()

      if (err.errorId === ApiErrorCode.CART_NOT_FOUND) {
        await recreateCart()
        return get({ isRecreated: true })
      }
      const extraInfoMessage = getExtraInfoMessage(err)

      const notificationLevel = [ApiErrorCode.ITEM_NOT_AVAILABLE, ApiErrorCode.NO_PRICE, ApiErrorCode.INVALID_PRICE].includes(err.errorId) ? 'item' : 'top'

      setNotification('error', replaceAll(err.message, { extraInfoMessage }), notificationLevel)
    }
    finally {
      pending.value = false
    }
  }

  const hasCustomsProduct = computed(() => items.value.some((item) => item.isCustoms))
  const hasOnlyCustomsProduct = computed(() => items.value.every((item) => item.isCustoms))

  /**
   * Build the localized "max quantity reached" message from an `ADDED_MAX_QUANTITY` error.
   * Falls back to the generic copy if `maxAllowedQty` is not present on the error.
   * @param err - API error from the cart endpoint
   */
  const getMaxAllowedQtyMessage = (err: ApiError) => {
    try {
      // optional chaining here would cause Sonar Qube complexity to go up so use try instead
      const maxAllowedQty = err.response?._data.errorDetails[1]
        .additionalInfo[0]
        .argument
        .statusDetails
        .additionalDetails
        .maxAllowedQty

      // if, e.g. the whole statement doesn't throw an error, but maxAllowedQty is undefined
      if (!maxAllowedQty) throw new Error('maxAllowedQty is undefined')

      return replaceAll($t.cannotAddMoreSameItems, { maxAllowedQty })
    }
    catch {
      // have to do it in catch block because of eslint Unsafe usage of ReturnStatement in finally block
      return $t.apiMessages[ApiErrorCode.ADDED_MAX_QUANTITY]
    }
  }

  /**
   * Build the localized "max line items reached" message from an `ADDED_MAX_LINE_ITEMS` error.
   * Falls back to the generic copy if `basketMaxLineItemSize` is not present on the error.
   * @param err - API error from the cart endpoint
   */
  const getMaxLineItemsMessage = (err: ApiError) => {
    try {
      // optional chaining here would cause Sonar Qube complexity to go up so use try instead
      const basketMaxLineItemSize = err.response?._data.errorDetails[1]
        .additionalInfo[0]
        .argument
        .statusDetails
        .additionalDetails
        .basketMaxLineItemSize

      // if, e.g. the whole statement doesn't throw an error, but basketMaxLineItemSize is undefined
      if (!basketMaxLineItemSize) throw new Error('basketMaxLineItemSize is undefined')

      return replaceAll($t.cannotAddMoreItems, { basketMaxLineItemSize })
    }
    catch {
      // have to do it in catch block because of eslint Unsafe usage of ReturnStatement in finally block
      return $t.apiMessages[ApiErrorCode.ADDED_MAX_LINE_ITEMS]
    }
  }

  type AddItemPayload = {
    productId: string
    qty?: number
    upc?: string
    maxQty?: number
    precreatedCustomsCode?: string
    productImageURL?: string
    dummyCustomsPdpUrl?: string
  } | string

  /**
   * Add one or more items to the cart. Accepts product ids or full payloads (qty, upc, customs,
   * gift options). Bootstraps a basket via `$createWithItem` when none exists or when the call
   * is scoped to a wallet context (`applePayPdp` / `paypalPdp`); otherwise uses `$addItem`.
   * Pushes GTM `onAddToCart`, refreshes payment methods, and either opens the mini-cart or
   * shows a success notification depending on feature flags and route.
   *
   * Recovers from `INVALID_CUSTOMER` and `CART_NOT_FOUND` by recreating the basket; rethrows
   * `ITEM_OOS`, `ITEMS_OOS`, and `GWP_PRODUCT` so the caller (PDP) can retry / surface its own UI.
   *
   * @param products - single product id, full payload, or array of either
   * @param params - options bag (all fields optional)
   * @param params.addToCartContextKey - wallet context that forces a separate basket
   * @param params.suppressMiniCart - skip auto-open of the mini-cart even when the flag is on
   * @param params.suppressNotification - skip the success notification
   * @param params.extendedGtmConfig - extra fields merged into the GTM event
   * @param params.giftOption - digital gift card recipient / message
   * @param params.customAmount - custom price for gift cards
   * @param params.customAmount.amount - the gift-card amount selected by the shopper
   * @param params.storeId - pickup store id; sets `action=pickup` query param
   * @param params.recipeId - customs recipe id
   */
  const add = async (products: AddItemPayload | AddItemPayload[], params: {
    addToCartContextKey?: CartStoreContext
    suppressMiniCart?: boolean
    suppressNotification?: boolean
    extendedGtmConfig?: ProductContext
    giftOption?: GiftOption
    customAmount?: { amount?: number }
    storeId?: string
    recipeId?: string
  } = {}) => {
    const { $gtm } = useNuxtApp()
    const { trackEvent } = useOptimizely()
    let response = {} as any
    pending.value = true

    const {
      addToCartContextKey,
      suppressMiniCart,
      suppressNotification,
      extendedGtmConfig,
      giftOption,
      customAmount,
      storeId,
      recipeId,
    } = params

    try {
      const itemsToAdd = ([products].flat(Infinity) as AddItemPayload[]).map((product) => {
        const {
          productId,
          qty = 1,
          upc,
          maxQty,
          precreatedCustomsCode,
          productImageURL,
          dummyCustomsPdpUrl
        } = isString(product) ? { productId: product } : product
        return {
          productId,
          qty,
          upc,
          customAmount,
          maxQty: maxQty || 999999, // sentinel: backend treats this as "no per-item cap"
          storeId,
          recipeId,
          precreatedCustomsCode,
          productImageURL,
          pdpUrl: dummyCustomsPdpUrl,
          cartId: basketId.value,
          ...getDigitalGiftOption(giftOption)
        }
      })

      const query = {
        ...queryParams.value,
        ...customAmount,
        ...(storeId && { action: 'pickup', favStoreId: storeId })
      }

      if (addToCartContextKey === 'applePayPdp' || addToCartContextKey === 'paypalPdp' || !basketId.value) {
        response = await cart.$createWithItem(itemsToAdd, { query })
        basketId.value = response.id
      }
      else {
        response = await cart.$addItem(itemsToAdd, { query })
      }
      $gtm.push('cart.onAddToCart', response, itemsToAdd, {
        breadcrumbs: deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
        category: history.state.category,
        searchTerm: history.state.searchTerm ?? history.state.suggestedPhrase,
        quantity: 1,
        ...extendedGtmConfig
      })

      trackEvent('product_add', {
        total: response.totals.total,
        items: response,
        itemsToAdd
      })

      updateCartData(response)
      clearCollectionPointFromSessionStorage()
      getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits
      response.items.forEach(({ id, customerNotifications }) => {
        if (customerNotifications) {
          const bopisNotification = customerNotifications.find((customerNotification) => ['ChangedStoreId', 'StsToSthTransition'].includes(customerNotification.type))
          if (bopisNotification) {
            cachedCustomerNotifications.value[id] = [{
              ...bopisNotification,
              message: bopisNotification.message || $t.itemDeliveryDetailsChanged
            }]
          }
        }
      })

      // display the mini cart upon successful add-to-cart
      // when it's feature flag is enabled and not adding from PDP page
      if (showMiniCartOnAddProduct && !suppressMiniCart && !addToCartContextKey && !route.name?.toString().includes('cart')) {
        DialogMiniCart.open()
      }
      else if (!suppressNotification) {
        // get the last added product and show the success notification
        setNotification('success', replaceAll($t.successfullyAddedToCart, response.items[response.items.length - 1]))
      }
      // DynaTrace Real User Monitoring
      if ($dtrum) {
        $dtrum.then((dtrum) => {
          [products].flat().forEach((product) => {
            const id = (isString(product) ? { productId: product } : product).productId
            const item: CartItem = response?.items.find(({ productId }) => productId === id)
            if (!item) return
            // Create an action by entering it and getting the ID
            const actionId = dtrum.enterAction('Add To Cart')
            // Add the necessary action properties
            dtrum.addActionProperties(
              actionId,
              null,
              null,
              {
                productsku: id // product sku added to cart
              },
              {
                productprice: item.price.amount // product price as a number
              }
            )
            // leave the action to send the data
            dtrum.leaveAction(actionId)
          })
        })
      }
      selectedUpsells.value = []
      pending.value = false
      errorMessage.value = ''
    }
    catch (err) {
      assertApiError(err)
      errorMessage.value = err.message
      // basket has expired in the back end for guest user so reset it
      // re-addition to the basket to be handled by checkout team, ticket no: GLOBAL15-44946
      const errId = err?.errorId
      if (errId) {
        switch (errId) {
          // CAB/HOTFIX: Per https://digital.vfc.com/jira/browse/GLOBAL15-59506
          // Users are encountering this error regularly in PROD right now
          // this costing TBL a lot of money, we'll need to re-evaluate
          case ApiErrorCode.INVALID_CUSTOMER:
            await clearAndRecreateCart()
            break
          case ApiErrorCode.CART_NOT_FOUND:
            await recreateCart()
            await add(products, params)
            break
          case ApiErrorCode.ITEM_OOS:
            errorMessage.value = $t.thisItemCouldNotBeMovedToCart
            throw err
          case ApiErrorCode.ITEMS_OOS:
            // GLOBAL15-69442 propagating this error up in order to retry on PDP
            errorMessage.value = err.message
            throw err
          case ApiErrorCode.ADDED_MAX_QUANTITY:
            errorMessage.value = getMaxAllowedQtyMessage(err)
            break
          case ApiErrorCode.ADDED_MAX_LINE_ITEMS:
            errorMessage.value = getMaxLineItemsMessage(err)
            break
          case ApiErrorCode.GWP_PRODUCT:
            toast.add({
              props: {
                title: $t.somethingWentWrong,
                message: $t.itemCannotAddedToCart,
                type: 'error'
              }
            })
            throw err
          default:
            errorMessage.value = err.message
            throw err
        }
      }
      else {
        log.warn(err) // in case we are not handling the error above, we need more information about it
      }
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Remove an item from the cart, push GTM `onRemoveFromCart`, and refresh payment methods.
   * Recovers from `INVALID_CUSTOMER` and `CART_NOT_FOUND` by recreating the basket.
   * @param itemId - cart line item id to remove
   * @param upc - UPC of the variant (optional, used by some backends to disambiguate)
   */
  const remove = async (itemId: string, upc?: string) => {
    const { $gtm } = useNuxtApp()
    const { trackEvent } = useOptimizely()
    try {
      pending.value = true
      const response = await cart.$removeItem({
        cartId: basketId.value,
        itemId,
        upc
      })

      const product = items.value.find((item) => item.id === itemId)
      $gtm.push('cart.onRemoveFromCart', {
        id: basketId.value,
        currency: currency.value,
        items: [product]
      })

      // Remove cart item from GTM persistent storage
      if (product) removeGtmCartProductsMap(product.productId, product.masterId)
      trackEvent('product_remove', {
        total: response.totals.total,
        product
      })

      updateCartData(response)
      clearCollectionPointFromSessionStorage()
      getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits
    }
    catch (err) {
      assertApiError(err)

      if (err.errorId === ApiErrorCode.INVALID_CUSTOMER)
        clearAndRecreateCart()
      if (err.errorId === ApiErrorCode.CART_NOT_FOUND) {
        await recreateCart()
        return remove(itemId)
      }
      // Need to know more specific error message
      throw new Error('Unable to remove item from cart. Please try again later', {
        cause: err
      })
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Apply a loyalty voucher / reward to the cart and refresh payment methods (some methods have
   * min/max thresholds that change once a reward is applied).
   * @param rewardAmount - voucher identifier / amount string accepted by the cart API
   */
  const applyReward = async (rewardAmount: string) => {
    try {
      pending.value = true
      const response = await cart.$applyReward(basketId.value, { appliedLoyaltyVoucher: rewardAmount })
      updateCartData(response)
      getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits
    }
    catch (err) {
      assertApiError(err)
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Attach a payment instrument (credit card, gift card, PayPal, …) to the cart. Refreshes
   * applicable shipping methods first because adding an instrument can shift eligibility.
   * Maps `errorId` to the appropriate notification level (input vs top) for gift-card errors.
   * @param paymentMethodId - payment method code (e.g. 'CREDIT_CARD', 'GIFT_CARD', 'PAYPAL')
   * @param extraPayload - method-specific payload (token, captureContext, …)
   * @returns the cart payload returned by the backend, or undefined on error
   */
  const postPaymentInstruments = async (paymentMethodId: PaymentMethodCode, extraPayload?: any) => {
    try {
      pending.value = true
      await getApplicableShippingMethods()

      const response = await cart.$addPaymentInstrument(basketId.value, {
        payment_method_id: paymentMethodId,
        ...(extraPayload || {})
      }, queryParams.value)

      updateCartData(response, { emitUpdateEvent: false })
      errorMessage.value = ''

      return response
    }
    catch (err) {
      assertApiError(err)
      errorMessage.value = err.message
      const level: NotificationLevel = {
        [ApiErrorCode.PAYMENT_ERROR]: 'top',
        [ApiErrorCode.PAYMENT_FRAUD_VERIFICATION_EXCEPTION]: 'top',
        [ApiErrorCode.CANT_SHIP_THERE]: 'top',
        [ApiErrorCode.GC_INVALID2]: 'input',
        [ApiErrorCode.GC_ALREADY_APPLIED]: 'input',
        [ApiErrorCode.GC_NOT_ALLOWED]: 'input',
        [ApiErrorCode.GC_ZERO_BALANCE]: 'input'
      }[err.errorId]

      setNotification('error', err.message, level)
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Remove a payment instrument from the cart and refresh applicable shipping methods.
   * Errors are logged, not surfaced - this is best-effort cleanup.
   * @param payment_instrument_id - id of the instrument to remove
   */
  const deletePaymentInstruments = async (payment_instrument_id: string) => {
    try {
      pending.value = true
      await getApplicableShippingMethods()

      const response = await cart.$deletePaymentInstrument(basketId.value, {
        payment_instrument_id
      })

      updateCartData(response, { emitUpdateEvent: false })
      errorMessage.value = ''

      return response
    }
    catch (err) {
      log.warn(err)
    }
    finally {
      pending.value = false
    }
  }

  // full list of error conditions can be found here: https://digital.vfc.com/wiki/display/ECOM15/BOPIS+Error+Handling+-+List+of+Cases
  const handleCustomerNotifications = (customerNotifications: CustomerNotification[]) => {
    customerNotifications.forEach((notification: CustomerNotification) => {
      if ([
        'PickupToSthTransition',
        'PickupToStsTransition',
        'StsToSthTransition',
        'StsToPickupTransition',
        'STHToPickupTransition',
        'OrderShippingMethodDowngraded'
      ].includes(notification.type)) {
        setNotification('info', notification.message || $t.itemDeliveryMethodChanged, 'top')
      }
    })
  }

  /**
   * Change the quantity of an existing line item. Re-fetches applicable shipping methods first
   * (the update endpoint omits them from the response). Pushes the appropriate add/remove GTM
   * event based on direction. Recovers from `INVALID_CUSTOMER` and `CART_NOT_FOUND`; falls back
   * to `get()` on any other error to resync.
   * @param item - the line item being mutated (current state, used for diff)
   * @param qty - target quantity
   */
  const setItemQty = async (item: CartItem, qty: number) => {
    if (pending.value) return

    let cartItem = <CartItem>{}
    const { $gtm } = useNuxtApp()
    const { trackEvent } = useOptimizely()
    try {
      pending.value = true
      const payload: UpdateItemPayload = {
        cartId: basketId.value,
        itemId: item.id,
        maxQty: item.maxQty,
        productId: item.productId,
        qty,
        upc: item.upc
      }

      // need this check because Mule API does not accept empty string for update item call
      if (item.recipeId)
        payload.recipeId = item.recipeId

      let params = {}
      if (item.selectedShippingOption?.storeInfo?.id) {
        payload.storeId = item.selectedShippingOption.storeInfo?.id
        params = { action: 'pickup', favStoreId: payload.storeId }
      }
      // get and use applicableShippingMethods since the updateItem call is not returning it
      await getApplicableShippingMethods()

      const response: any = await cart.$updateItem(basketId.value, payload, params)
      cartItem = response.items.find(({ productId }) => productId === item.productId)

      const diff = Math.abs(item.qty - qty)
      if (qty > item.qty) {
        $gtm.push('cart.onAddToCart', response, [item], {
          breadcrumbs: deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
          category: history.state.category,
          searchTerm: history.state.searchTerm,
          quantity: diff
        })
        // Mirror the GA4 addToCart push to Optimizely so cart-page quantity
        // increments are counted as product_add events.
        trackEvent('product_add', {
          total: response.totals.total,
          items: response,
          itemsToAdd: [{ ...item, qty: diff }]
        })
      }
      else {
        $gtm.push('cart.onRemoveFromCart', {
          id: basketId.value,
          currency: currency.value,
          items: [item]
        }, { quantity: diff })
      }

      updateCartData(response)
      clearCollectionPointFromSessionStorage()
      getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits

      toast.remove('quantity-change')
      toast.add({
        id: 'quantity-change',
        props: {
          message: replaceAll($t.cartItemQuantityEdited, { name: item.name, qty }),
          type: 'success'
        }
      })

      errorMessage.value = ''
    }
    catch (err) {
      assertApiError(err)
      if (err.errorId === ApiErrorCode.INVALID_CUSTOMER)
        clearAndRecreateCart()
      if (err.errorId === ApiErrorCode.CART_NOT_FOUND) {
        await recreateCart()
        return setItemQty(item, qty)
      }
      if (err.errorId === ApiErrorCode.ADDED_MAX_QUANTITY) {
        toast.add({
          props: {
            message: getMaxAllowedQtyMessage(err),
            type: 'error'
          }
        })
      }
      // GWP qty is promotion-driven server-side; backend rejects manual changes. Notify the user
      // but skip the get() resync below - server cart state did not move.
      if (err.errorId === ApiErrorCode.GWP_PRODUCT) {
        setNotification('error', $t.maximumItemQtyWhileInCart, 'top')
        return
      }

      await get()
    }
    finally {
      pending.value = false
      if (cartItem.customerNotifications)
        handleCustomerNotifications(cartItem.customerNotifications)
    }
  }

  /**
   * Set the billing address on the cart. Phone is normalised to E.164 before being sent.
   * Newsletter consent is forced to false here - opt-ins are managed through dedicated forms.
   * @param address - billing address payload (includes country-coded phone)
   */
  const addBillingAddress = async (address: CartStoreAddress) => {
    const response = await cart.$addBillingAddress(basketId.value, {
      ...address,
      phone: formatE164phone(address.phone, address.phoneCode),
      subscriptions: {
        newsletterConsent: false
      }
    })

    updateCartData(response, { emitUpdateEvent: false })
  }

  /**
   * Promote an item from the save-for-later list back into the active cart. Bootstraps a basket
   * if none exists. When a favorite store is set, performs a full `get()` with `isPatchRequired`
   * so pickup eligibility is recomputed; otherwise updates from the add-item response directly.
   * @param item - the saved cart item being restored
   * @param item.itemId - cart line item id
   * @param item.maxQty - per-item quantity cap
   * @param item.pdpUrl - PDP slug, sent for customs items
   * @param item.productId - master / variant product id
   * @param item.productImageURL - thumbnail, sent for customs items
   * @param item.recipeId - customs recipe id (omit for non-customs)
   * @param item.qty - quantity to restore
   * @param item.upc - UPC of the variant
   * @param saveForLaterId - id of the save-for-later list owning the item
   */
  const moveSavedItemBackToCart = async (
    { itemId, maxQty, pdpUrl, productId, productImageURL, recipeId, qty, upc }: CartItem,
    saveForLaterId: string
  ) => {
    const auth = useAuthStore()

    try {
      pending.value = true

      if (!basketId.value) {
        const response = await cart.$create({})
        basketId.value = response?.id || ''
      }

      const data = await cart.$addItem({
        // @ts-expect-error incorrect contract type
        cartId: basketId.value,
        productId,
        qty,
        recipeId,
        maxQty,
        upc,
        saveForLater: {
          consumerId: auth.consumerId || undefined,
          itemId,
          saveForLaterId
        },
        ...(recipeId && { pdpUrl, productImageURL })
      })
      if (favoriteStoreId.value) {
        await get({ isPatchRequired: true })
      }
      else {
        updateCartData(data)
        getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits
      }
      clearCollectionPointFromSessionStorage()
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Set shipping addresses on the cart (one per shipment). Pre-fetches applicable shipping
   * methods if missing, runs reCAPTCHA when the flag is on, and refreshes payment methods
   * afterwards if any shipment turned into pickup/STS (some methods become disabled for CP).
   * @param shippingAddress - one address per shipment
   * @param recaptcha - active reCAPTCHA helper from `useRecaptcha`
   * @param context - Payment method context used by collection point and express wallet flows
   * @param bid - 'cookie' to reuse the cookie-bound basket id (Apple Pay PDP / PayPal PDP)
   */
  const addShippingAddress = async (
    shippingAddress: ShippingAddress[],
    recaptcha: ReturnType<typeof useRecaptcha>,
    context?: 'COLLECTION' | 'APPLE_PAY_EXPRESS' | 'SHOPPAY_EXPRESS',
    bid?: 'cookie',
  ) => {
    // if we don't have applicable shipping methods, we need to get them first
    if (!shippingMethods.value[0]?.applicableShippingMethods)
      await getApplicableShippingMethods()

    try {
      pendingShippingAddress.value = true
      const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
      const payload = shippingAddress.map((sh) => ({
        ...sh,
        phone: formatE164phone(sh.phone, sh.phoneCode),
        ...captchaResponse && { recaptcha_response: captchaResponse }
      }))
      // @ts-expect-error incorrect contract type
      const response = await cart.$addShippingAddress(basketId.value, payload, { context, bid })
      updateCartData(response, { emitUpdateEvent: false })
      if (shippingMethods.value?.some(isPickupOrSts)) {
        getPaymentMethod() // Refresh the payment methods section for CP address. Some may become disabled based on selected shipping address.
      }
      if (response.customerNotifications)
        handleCustomerNotifications(response.customerNotifications)
      return response
    }
    finally {
      pendingShippingAddress.value = false
    }
  }
  /**
   * Select a shipping rate for a given shipping group (ME / customs / prime).
   * Marked as shopper-applied so the backend stops auto-defaulting on subsequent updates.
   * Refreshes payment methods afterwards (some methods are bounded by total).
   * @param code - shipping method code (e.g. 'STANDARD', 'EXPRESS', 'PICKUP')
   * @param shippingId - target shipping group, defaults to 'me' (main shipment)
   * @param context - Express wallet context used to recalculate payment-specific totals
   */
  const updateShippingMethod = async (
    code: ShippingMethodCode,
    shippingId: ShippingMethod['shippingId'] = 'me',
    context?: 'SHOPPAY_EXPRESS',
  ) => {
    try {
      pendingShippingAddress.value = true
      const payload = {
        cartId: basketId.value,
        code,
        shippingId,
        shopperAppliedSM: true
      }
      const response = context
        ? await cart.$setShippingMethod(payload, { context })
        : await cart.$setShippingMethod(payload)
      updateCartData(response, {
        emitUpdateEvent: false,
        keepNotifications: !!notification.message
      })
      getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits
      errorMessage.value = ''
    }
    catch (err) {
      assertApiError(err)
      setNotification('error', err.message, 'top')
    }
    finally {
      pendingShippingAddress.value = false
    }
  }

  const itemsPerShippingMethod = computed(() => {
    // Two-pass stable sort: pass 1 pushes pickup/STS to the end; pass 2 promotes bonus items
    // to the front when the brand opts in (returning 0 keeps pass-1 ordering otherwise).
    const sortedItems = [...items.value]
      .toSorted((a, b) => {
        const isAPickup = isPickupOrSts(a.selectedShippingOption?.shippingMethod.code)
        const isBPickup = isPickupOrSts(b.selectedShippingOption?.shippingMethod.code)

        if (isAPickup === isBPickup)
          return 0

        return isAPickup ? 1 : -1
      })
      .toSorted((a, b) => sortBonusItemsFirst ? +b.bonus - +a.bonus : 0)

    const groups: CartGroupedItems = sortedItems.reduce((gp, it) => {
      let groupKey: 'digital' | 'pickup' | 'home'
      if (isDigitalDelivery(it.selectedShippingOption?.shippingMethod.code)) {
        groupKey = 'digital'
      }
      else {
        groupKey = isPickupOrSts(it.selectedShippingOption?.shippingMethod.code) ? 'pickup' : 'home'
      }

      if (!gp[groupKey])
        gp[groupKey] = { items: [] }

      gp[groupKey]?.items.push(it)

      return gp
    }, {})

    return groups
  })

  /**
   * Page the save-for-later list. When the `allowSaveForLater` feature flag is off, resolves
   * to an empty list without hitting the API. When `start > 0` the result is appended to the
   * existing items (infinite-scroll style); otherwise it replaces the list.
   * @param start - zero-based offset (default 0)
   * @param count - page size (default `savedForLaterPageSize` from app config)
   */
  const fetchSavedCartItems = async (start = 0, count = savedForLaterPageSize) => {
    if (!$feature.allowSaveForLater) {
      savedCartPending.value = true
      try {
        savedItems.value = {
          ...(savedItems.value as SavedItems),
          items: [],
          count: 0,
          total: 0
        } as SavedItems
      }
      finally {
        savedCartPending.value = false
      }
      return
    }

    try {
      savedCartPending.value = true
      // Buy-in-store employee sessions impersonate the customer for cart actions but must not
      // surface the customer's saved-for-later list - short-circuit to an empty result.
      const { saveForLater = [] } = !buyInStore.employeeConnected
        ? await wishlists.$getSavedForLater({ query: { start, count } })
        : { saveForLater: [] }
      const [savedItemsValue] = saveForLater as SavedItems[]
      savedItems.value = savedItems.value?.items && start > 0
        ? {
            ...savedItemsValue,
            items: [...savedItems.value.items, ...savedItemsValue.items]
          }
        : savedItemsValue
    }
    finally {
      savedCartPending.value = false
    }
  }
  /**
   * Apply a promo / coupon code to the cart and refresh applicable payment methods.
   * Errors are logged but never rethrown - UI relies on the response promo summary, not throws.
   * @param promoCode - promotion code as entered by the shopper
   */
  const applyCoupon = async (promoCode: string) => {
    try {
      const response = await cart.$applyCoupon({
        cartId: basketId.value,
        code: promoCode
      }, { ...queryParams.value })
      updateCartData(response)
      getPaymentMethod() // Refresh the applicable payment methods list to account for ones with min/max total limits
    }
    catch (err) {
      log.error(err)
    }
  }

  const approachingDiscount = computed(() => {
    const promotions = enableApproachingDiscounts ? approachingPromotions.get('cart') : []

    // Pure pickup/STS orders never incur shipping, so shipping-class promos are unreachable —
    // hiding them prevents a progress bar that can't move.
    const { conditionThreshold, distanceFromConditionThreshold } = (
      shippingMethods.value.every(isPickupOrSts)
        ? promotions.filter(({ promotionClass }) => promotionClass !== 'SHIPPING')
        : promotions).find(
      ({ conditionThreshold, distanceFromConditionThreshold }) => conditionThreshold && distanceFromConditionThreshold
    ) || {}

    if (!conditionThreshold) return

    return {
      total: $formatPrice(conditionThreshold, currency.value),
      current: (conditionThreshold - (distanceFromConditionThreshold || 0)) / conditionThreshold * 100,
    }
  })

  return {
    add,
    addBillingAddress,
    addShippingAddress,
    applyCoupon,
    applyReward,
    approachingDiscount,
    athletePayment: computed(() => paymentInstruments.value.find((item) => item.payment_method_id === 'ATHLETES_PAYMENT')),
    creditedConsumerPayment: computed(() => paymentInstruments.value.find((item) => item.payment_method_id === 'INFLUENCER_CREDIT')),
    basketId,
    cachedCustomerNotifications,
    clearNotification,
    clearCollectionPointFromSessionStorage,
    couponItems,
    currency,
    // defaultShippingMethod: should be ship to home
    defaultShippingMethod: computed(() => shippingMethods.value.find(({ shippingId }) => shippingId === 'me')),
    deletePaymentInstruments,
    errorMessage,
    favoriteStoreId,
    fetchSavedCartItems,
    flash,
    get,
    getApplicableShippingMethods,
    getMaxAllowedQtyMessage,
    getMaxLineItemsMessage,
    getPaymentMethod,
    giftCards: computed(() => paymentInstruments.value.filter((item) => item.payment_method_id === 'GIFT_CARD')),
    giftCardsBalances,
    handleFlashMessages,
    hasCollectionPointItems: computed(() => isPickupOrSts(shippingMethods.value?.[0])),
    hasCustomsProduct,
    hasSthItems: computed(() => !shippingMethods.value.filter(isPhysicalDelivery).every(isPickupOrSts)),
    hasDigitalItems: computed(() => shippingMethods.value.some(isDigitalDelivery)),
    hasDigitalItemsOnly: computed(() => shippingMethods.value.every(isDigitalDelivery)),
    hasOnlyCustomsProduct,
    hasPickupItems: computed(() => shippingMethods.value.some(isPickupOrSts)),
    hasPickupItemsOnly: computed(() => shippingMethods.value.every(isPickupOrSts)),
    isCartMerged,
    primeShippingSelected: computed(() => items.value.some(({ shippingOptions }) => shippingOptions.find(({ selected, shippingMethod: { code } }) => selected && code === 'prime'))),
    isZeroOrder: computed(() => totals.value.total === 0 && !paymentInstruments.value.length),
    items,
    itemsPerShippingMethod,
    loadingApplicableShippingMethods,
    loyaltyPayment: computed(() => paymentInstruments.value.find((item) => item.payment_method_id === 'LOYALTY_POINTS')),
    moveSavedItemBackToCart,
    notification,
    orderPromoSummary,
    orderPromotions,
    outOfStockItems,
    paymentInstruments,
    paymentMethods,
    paymentMethodsPending,
    pending,
    pendingShippingAddress,
    postPaymentInstruments,
    productPromoSummary,
    remove,
    reset,
    resetFlashMsg,
    rewards: computed(() => paymentInstruments.value.filter((item) => loyaltyType.includes(item.payment_method_id))),
    savedCartPending,
    savedItems,
    selectedUpsells,
    upsellProducts,
    setItemQty,
    setNotification,
    shippingMethods,
    shippingPromoSummary,
    promoSummary,
    totalItems,
    totals,
    updateCartData,
    updateItemShippingMethod,
    updateShippingMethod,
  }
}, {
  // Scoped contexts (applePayPdp, paypalPdp, …) are single-flow ephemeral baskets; persisting
  // them across reloads would leak the wallet basket back into the main cart.
  persist:
    storeType
      ? undefined
      : {
          pick: [
            'basketId',
            'currency',
            'totalItems',
            'totals',
            'items',
            'shippingMethods',
            'paymentMethods',
            'pending',
            'error',
            'errorMessage',
            'flash',
            'favoriteStoreId',
            'cachedCustomerNotifications',
          ]
        }
})()
